import { useEffect, useState } from "react";
import useFetch from "../../hooks/useFetch";
import TestsAPI from "../../_api/_testsAPI";
import Big5Intro from "./Big5Intro";
import Big5Result from "./Big5Result";
import Big5Test from "./Big5Test";
import { TestType } from "../../@types/testType";
import { Button, Card, CardContent, Dialog, Typography } from "@mui/material";
import { useSelector, shallowEqual } from "react-redux";
import SplashScreenTest from "../common/SplashScreenTest";
import { useLocation, useNavigate } from "react-router-dom";
import { Utils } from "../../Utils";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { useTranslation } from "react-i18next";

const Big5Container: React.FC<{
  onboarding?: boolean;
  nextStep?: any;
  onboardingStep?: number;
  lastStep?: boolean;
  canSkip?: boolean;
  handlePrevious?: () => void;
}> = ({
  onboarding,
  nextStep,
  onboardingStep,
  lastStep,
  handlePrevious,
  canSkip,
}) => {
  const { t } = useTranslation("common");
  const [step, setStep] = useState(0);
  const [realodCall, setRealodCall] = useState(false);
  const [openSplashScreen, setOpenSplashScreen] = useState(false);
  const navigate = useNavigate();

  const { data, loading } = useFetch(
    TestsAPI.getById("b5/result", { reload: realodCall })
  );

  const useQuery = () => {
    return new URLSearchParams(useLocation().search);
  };
  let query: any = useQuery();
  const isIntro = query.get("intro") === "true";
  const isTest = query.get("test") === "true";
  const isResult = query.get("result") === "true";

  const [result, setResult] = useState<TestType>(data);
  const tokenData = useSelector((state: any) => state.user, shallowEqual);

  const updateSTep = () => {
    if (step + 1 === 2) {
      setRealodCall(true);
      setOpenSplashScreen(true);
      setTimeout(() => {
        setOpenSplashScreen(false);
      }, 6500);
    }
    setStep(step + 1);
  };

  const abortTest = () => {
    try {
      localStorage.removeItem(`big5_pending_${tokenData?.person_id}`);
      localStorage.removeItem(`big5_progress_${tokenData?.person_id}`);
    } catch (err) {}
    setStep(result?.isPassed ? 2 : 0);
  };

  const resetResult = () => {
    const newResults = { ...result, results: null };
    setResult(newResults);
    setStep(0);
  };

  const skipStep = () => {
    const newState: any =
      Utils.getCookie(`${tokenData.person_id}_do_later_step`) || [];
    newState?.push("personality");
    Utils.setLongCookies(`${tokenData.person_id}_do_later_step`, newState);
    nextStep();
  };

  useEffect(() => {
    const big5_pending = localStorage.getItem(
      `big5_pending_${tokenData?.person_id}`
    );
    big5_pending && setStep(1);
    !loading && setResult(data);
  }, [data, loading, tokenData]);

  useEffect(() => {
    if (isIntro || isTest) {
      switch (step) {
        case 0:
          return navigate("/app/big5?intro=true", { replace: true });
        case 1:
          return navigate("/app/big5?test=true", { replace: true });
        case 2:
          return navigate("/app/big5?result=true", { replace: true });
        default:
          break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  return (
    <>
      {(step === 0 && !result?.results) || isIntro ? (
        <>
          {onboardingStep && onboardingStep > 1 && (
            <Button onClick={handlePrevious} variant="outlined">
              <KeyboardBackspaceIcon sx={{ fontSize: "20px" }} />
              <Typography variant="body2" sx={{ ml: 1 }}>
                {t("onboarding.previous_btn")}
              </Typography>
            </Button>
          )}
          <Big5Intro
            nextStep={updateSTep}
            skipTest={canSkip ? skipStep : handlePrevious}
            onboarding={onboarding}
            onboardingStep={onboardingStep}
            canSkip={canSkip}
          />
        </>
      ) : (step === 1 && !result?.results) || isTest ? (
        onboarding ? (
          <Big5Test
            resultsSent={updateSTep}
            nextStep={canSkip ? nextStep : handlePrevious}
            abortTest={abortTest}
            onboarding={onboarding}
            personId={tokenData?.person_id}
          />
        ) : (
          <Card>
            <CardContent>
              <Big5Test
                resultsSent={updateSTep}
                abortTest={abortTest}
                personId={tokenData?.person_id}
              />
            </CardContent>
          </Card>
        )
      ) : (
        (step === 2 || result?.results?.results || isResult) &&
        !openSplashScreen && (
          <>
            {onboardingStep && onboardingStep > 1 && (
              <Button onClick={handlePrevious} variant="outlined">
                <KeyboardBackspaceIcon sx={{ fontSize: "20px" }} />
                <Typography variant="body2" sx={{ ml: 1 }}>
                  {t("onboarding.previous_btn")}
                </Typography>
              </Button>
            )}
            <Big5Result
              result={result}
              results={result?.results?.results}
              traits={result?.results?.traits}
              deleteResult={resetResult}
              retryTest={resetResult}
              onboarding={onboarding}
              lastStep={lastStep}
              nextStep={nextStep}
            />
          </>
        )
      )}
      <Dialog open={openSplashScreen} transitionDuration={{ exit: 1250 }}>
        <SplashScreenTest test="b5" />
      </Dialog>
    </>
  );
};

export default Big5Container;
